// ── Gift helpers ──────────────────────────────────────────────────────────────
// GiftCache is declared once here. If cart.js also needs it, move this block
// to a shared file (e.g. gift-helpers.js) and load it before both scripts.
const GiftCache = { productByHandle: new Map() };

async function fetchProductJsonByHandle(handle) {
  if (!handle) return null;
  const key = String(handle).trim();
  if (GiftCache.productByHandle.has(key)) return GiftCache.productByHandle.get(key);

  const res = await fetch(`/products/${key}.js`, { credentials: 'same-origin' });
  if (!res.ok) return null;

  const json = await res.json();
  GiftCache.productByHandle.set(key, json);
  return json;
}

function giftTypeFromHandle(handle) {
  const h = (handle || '').toLowerCase();
  if (h.includes('insole')) return 'insoles';
  if (h.includes('bag')) return 'bag';
  return null;
}

function normalize(str) {
  return (str || '').toString().trim().toLowerCase();
}

function pickSingleVariantId(productJson) {
  if (!productJson || !Array.isArray(productJson.variants) || !productJson.variants.length) return null;
  const firstAvail = productJson.variants.find(v => v.available) || productJson.variants[0];
  return firstAvail ? firstAvail.id : null;
}

function findInsoleVariantId(productJson, gender, shoeSizeNum) {
  if (!productJson || !Array.isArray(productJson.variants)) return null;
  if (!gender || !shoeSizeNum) return null;

  const expected = `${gender} us ${shoeSizeNum}`.toLowerCase();

  let v = productJson.variants.find(vr => normalize(vr.title) === expected);
  if (v) return v.id;

  v = productJson.variants.find(vr => normalize(vr.title).includes(expected));
  if (v) return v.id;

  v = productJson.variants.find(vr => {
    const t = normalize(vr.title);
    return t.includes(gender.toLowerCase()) && t.includes(`us ${shoeSizeNum}`);
  });
  if (v) return v.id;

  const firstAvail = productJson.variants.find(vr => vr.available) || productJson.variants[0];
  return firstAvail ? firstAvail.id : null;
}

async function buildGiftItemFromHandle({ handle, parentVariantId, parentQty, productTitle, formEl }) {
  const type = giftTypeFromHandle(handle);
  if (!type) return null;

  const giftProduct = await fetchProductJsonByHandle(handle);
  if (!giftProduct) return null;

  let giftVariantId = null;

  if (type === 'bag') {
    giftVariantId = pickSingleVariantId(giftProduct);
  }

  if (type === 'insoles') {
    const mainProductHandle = formEl.closest('product-form')?.dataset.productHandle;
    const mainProduct = await fetchProductJsonByHandle(mainProductHandle);
    const mainVariant = mainProduct?.variants?.find(v => v.id === parentVariantId);

    if (!mainVariant) return null;

    const shoeSizeNum = mainVariant.option1;
    const gender = productTitle.toLowerCase().includes('women') ? 'Women' : 'Men';

    giftVariantId = findInsoleVariantId(giftProduct, gender, shoeSizeNum);
  }

  if (!giftVariantId) return null;

  return {
    id: Number(giftVariantId),
    quantity: parentQty,
    properties: {
      _gift: 'true',
      _gift_type: type,
      _gift_handle: handle,
      _gift_for_variant: String(parentVariantId)
    }
  };
}

// ── ProductForm custom element ────────────────────────────────────────────────
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.forceCheckout = this.dataset.forceCheckout === 'true';
      }

      // ── Insole addon helper ──────────────────────────────────────────────────
      async _resolveInsoleItem() {
        const module = document.querySelector('#insole-addon-module') || document.querySelector('[data-addon-v2]');
        if (!module) return null;

        let variants;
        try {
          variants = JSON.parse(module.dataset.upsellVariants || '[]');
        } catch (e) {
          console.warn('[ProductForm] Failed to parse upsell variants:', e);
          return null;
        }

        if (!variants.length) {
          console.warn('[ProductForm] No upsell variants found on module.');
          return null;
        }

        const mainVariantId = Number(this.form.querySelector('[name="id"]')?.value);
        if (!mainVariantId) return null;

        let addonProperties = {};
        if (module.dataset.addonType == 'free') {
          addonProperties = {
            _gift: 'true',
            _gift_type: 'insole_addon',
            _gift_for_variant: String(mainVariantId)
          };
        }

        return this._matchShoeSize(variants, addonProperties);
      }

      // Free insole (the block's gift row, e.g. the Fluffy with a Suede Clog pre-order): one per pair, in the shoe's size.
      async _resolveFreeInsoleItem(qty) {
        const module = document.querySelector('#insole-addon-module');
        if (!module?.dataset.freeVariants) return null;

        let variants;
        try {
          variants = JSON.parse(module.dataset.freeVariants);
        } catch (e) {
          console.warn('[ProductForm] Failed to parse free insole variants:', e);
          return null;
        }
        if (!Array.isArray(variants) || !variants.length) return null;

        const mainVariantId = Number(this.form.querySelector('[name="id"]')?.value);
        if (!mainVariantId) return null;

        const item = await this._matchShoeSize(variants, {
          _gift: 'true',
          _gift_type: 'insole_free',
          _gift_for_variant: String(mainVariantId)
        });
        if (item) item.quantity = qty;
        return item;
      }

      // The page's product handle. The sticky bar's form has none, so it reads the main form's.
      _pageProductHandle() {
        return this.dataset.productHandle
          || document.querySelector('product-form[data-product-handle]')?.dataset.productHandle;
      }

      // Second add-on row (e.g. the Fluffy beside an included Ortho): one per pair, in the shoe's size, when ticked.
      async _resolveAddon2Item(qty) {
        const module = document.querySelector('#insole-addon-module');
        if (!module?.dataset.addon2Variants) return null;
        if (!module.querySelector('input[name="insole-addon2"]:checked')) return null;

        let variants;
        try {
          variants = JSON.parse(module.dataset.addon2Variants);
        } catch (e) {
          console.warn('[ProductForm] Failed to parse second add-on variants:', e);
          return null;
        }
        if (!Array.isArray(variants) || !variants.length) return null;

        const item = await this._matchShoeSize(variants, {});
        if (item) item.quantity = qty;
        return item;
      }

      // Bundle swap: the block can sell a bundle in place of the page's product, in the same colour and size,
      // so the page, its URL and its reviews stay as they are. With no match the page's own product is added.
      async _resolveSwapVariantId(mainVariantId) {
        const module = document.querySelector('#insole-addon-module');
        if (module?.dataset.swapActive !== 'true' || !module.dataset.swapVariants) return null;

        try {
          const variants = JSON.parse(module.dataset.swapVariants);
          const mainProduct = await fetch(`/products/${this._pageProductHandle()}.js`).then(r => r.json());
          const mainVariant = mainProduct?.variants?.find(v => v.id === mainVariantId);
          if (!mainVariant) return null;

          const matched = variants.find(v => v.option1 === mainVariant.option1 && v.option2 === mainVariant.option2);
          if (!matched) console.warn(`[ProductForm] No bundle variant for "${mainVariant.title}", adding the product itself.`);
          return matched ? Number(matched.id) : null;
        } catch (e) {
          console.warn('[ProductForm] Failed to resolve the bundle swap:', e);
          return null;
        }
      }

      // The insole variant whose size (option1) equals the selected shoe's size (option2).
      async _matchShoeSize(variants, addonProperties) {
        const mainVariantId = Number(this.form.querySelector('[name="id"]')?.value);
        const mainProductHandle = this._pageProductHandle();
        if (!mainProductHandle) {
          console.warn('[ProductForm] No data-product-handle found on product-form.');
          return null;
        }

        try {
          const mainProduct = await fetch(`/products/${mainProductHandle}.js`).then(r => r.json());
          const mainVariant = mainProduct?.variants?.find(v => v.id === mainVariantId);
          if (!mainVariant) {
            console.warn('[ProductForm] Could not find main variant in product JSON.');
            return null;
          }

          const shoeSize = String(mainVariant.option2).trim();
          // console.log('[ProductForm] Attempting to match shoeSize:', shoeSize);

          const matched = variants.find(v => String(v.option1).trim() === shoeSize);

          if (!matched) {
            const fallback = variants.find(v => v.available) || variants[0];
            console.warn(`[ProductForm] No insole variant matched "${shoeSize}", using fallback:`, fallback?.title);
            return fallback
              ? { id: Number(fallback.id), quantity: 1, properties: addonProperties }
              : null;
          }

          return { id: Number(matched.id), quantity: 1, properties: addonProperties };
        } catch (e) {
          console.warn('[ProductForm] Failed to fetch main product JSON for insole matching:', e);
          return null;
        }
      }

      async getUnisexInsoleItem(skipRadioCheck = false) {

        const module = document.querySelector('#insole-addon-module');
        if (!module) {
          console.warn('[ProductForm] getUnisexInsoleItem: #insole-addon-module not found');
          return null;
        }

        if (!skipRadioCheck) {
          const opt2Radio = module.querySelector('input[name="insole-addon"][value="opt2"]');
          console.log('[ProductForm] opt2Radio:', opt2Radio, 'checked:', opt2Radio?.checked);
          if (!opt2Radio?.checked) {
            console.warn('[ProductForm] getUnisexInsoleItem: opt2 not checked, returning null');
            return null;
          }
        }

        return this._resolveInsoleItem();
      }

      // V2 — gate already handled by popup confirm/decline, just resolve
      async getV2InsoleItem() {
        return this._resolveInsoleItem();
      }

      // Wash bag addon (popup 2) — single-variant style, no size matching needed
      async _resolveBagItem() {
        const module = document.querySelector('#insole-addon-module');
        if (!module) return null;

        let variants;
        try {
          variants = JSON.parse(module.dataset.bagVariants || '[]');
        } catch (e) {
          console.warn('[ProductForm] Failed to parse bag variants:', e);
          return null;
        }

        if (!variants.length) return null;

        const bagVariantId = pickSingleVariantId({ variants });
        if (!bagVariantId) return null;

        return {
          id: Number(bagVariantId),
          quantity: 1,
          properties: { _addon: 'true', _addon_type: 'wash_bag' }
        };
      }

      // Insole + wash bag bundle product (popup 2 "bundle" choice) — sized by
      // shoe size like the insole, so it needs the same match-against-main-variant
      // logic as _resolveInsoleItem(). Only used when a bundle product is
      // configured on the block; otherwise the caller falls back to adding the
      // insole and wash bag as two separate items.
      async _resolveBundleItem() {
        const module = document.querySelector('#insole-addon-module');
        if (!module) return null;

        let variants;
        try {
          variants = JSON.parse(module.dataset.bundleVariants || '[]');
        } catch (e) {
          console.warn('[ProductForm] Failed to parse bundle variants:', e);
          return null;
        }

        if (!variants.length) return null;

        const mainVariantId = Number(this.form.querySelector('[name="id"]')?.value);
        if (!mainVariantId) return null;

        const mainProductHandle = this.dataset.productHandle;
        if (!mainProductHandle) {
          console.warn('[ProductForm] No data-product-handle found on product-form.');
          return null;
        }

        try {
          const mainProduct = await fetch(`/products/${mainProductHandle}.js`).then(r => r.json());
          const mainVariant = mainProduct?.variants?.find(v => v.id === mainVariantId);
          if (!mainVariant) {
            console.warn('[ProductForm] Could not find main variant in product JSON.');
            return null;
          }

          const shoeSize = String(mainVariant.option2).trim();
          const matched = variants.find(v => String(v.option1).trim() === shoeSize);

          if (!matched) {
            const fallback = variants.find(v => v.available) || variants[0];
            console.warn(`[ProductForm] No bundle variant matched "${shoeSize}", using fallback:`, fallback?.title);
            return fallback
              ? { id: Number(fallback.id), quantity: 1, properties: { _bundle: 'true' } }
              : null;
          }

          return { id: Number(matched.id), quantity: 1, properties: { _bundle: 'true' } };
        } catch (e) {
          console.warn('[ProductForm] Failed to fetch main product JSON for bundle matching:', e);
          return null;
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // ── Insole addon gating ─────────────────────────────────────────────────
        const v1Module = document.querySelector('#insole-addon-module');
        const popupMode = v1Module?.dataset.popupMode || 'popup2';
        const v1Opt2Checked = v1Module?.querySelector('input[name="insole-addon"][value="opt2"]')?.checked ?? false;
        const bagConfigured = (() => {
          try { return JSON.parse(v1Module?.dataset.bagVariants || '[]').length > 0; }
          catch (e) { return false; }
        })();
        const bundleConfigured = (() => {
          try { return JSON.parse(v1Module?.dataset.bundleVariants || '[]').length > 0; }
          catch (e) { return false; }
        })();

        if (popupMode === 'popup1') {
          // Popup 1 only — no Popup 2 step in this mode, ever, regardless of
          // whether a wash bag/bundle is configured on the block.
          if (v1Module && !v1Opt2Checked && !evt._insoleV1Confirmed) {

            document.addEventListener('insole-v1:confirm', (e) => {
              const confirmed = new Event('submit', { bubbles: true, cancelable: true });
              confirmed._insoleV1Confirmed = true;
              confirmed._includeInsole = e.detail?.includeInsole ?? false;
              this.form.dispatchEvent(confirmed);
            }, { once: true });

            v1Module.dispatchEvent(new CustomEvent('insole-v1:open', { bubbles: false }));
            return;
          }
        } else if (popupMode === 'popup3') {
          // Single-popup flow — Popup 1 is never shown, regardless of checkbox state.
          // If no wash bag is configured there's nothing to build a ladder from, so
          // fall through to plain add-to-cart (checkbox-driven), same as today's
          // bagConfigured-false fallback below.
          if (v1Module && bagConfigured && !evt._popup2Confirmed) {
            document.addEventListener('insole-popup2:confirm', (e) => {
              const confirmed = new Event('submit', { bubbles: true, cancelable: true });
              confirmed._popup2Confirmed = true;
              confirmed._includeInsole = e.detail?.includeInsole ?? false;
              confirmed._includeBag = e.detail?.includeBag ?? false;
              this.form.dispatchEvent(confirmed);
            }, { once: true });

            v1Module.dispatchEvent(new CustomEvent('insole-popup2:open', {
              bubbles: false,
              detail: { insoleCommitted: v1Opt2Checked } // reflect the checkbox, same as the popup2 branch
            }));
            return;
          }
        } else {
          // ── Insole V1 popup intercept ───────────────────────────────────────
          if (v1Module && !v1Opt2Checked && !evt._insoleV1Confirmed) {

            document.addEventListener('insole-v1:confirm', (e) => {
              const confirmed = new Event('submit', { bubbles: true, cancelable: true });
              confirmed._insoleV1Confirmed = true;
              confirmed._includeInsole = e.detail?.includeInsole ?? false;
              this.form.dispatchEvent(confirmed);
            }, { once: true });

            v1Module.dispatchEvent(new CustomEvent('insole-v1:open', { bubbles: false }));
            return;
          }
          // ───────────────────────────────────────────────────────────────────

          // ── Insole Popup 2 intercept (final bundle gate) ────────────────────
          // Only applies when a wash bag product is configured on the block —
          // otherwise falls through untouched to preserve today's Popup-1-only
          // behavior for products without a bag add-on set up.
          if (v1Module && bagConfigured && !evt._popup2Confirmed) {
            const insoleCommittedSoFar = v1Opt2Checked || (!!evt._insoleV1Confirmed && !!evt._includeInsole);

            document.addEventListener('insole-popup2:confirm', (e) => {
              const confirmed = new Event('submit', { bubbles: true, cancelable: true });
              // Carry _insoleV1Confirmed forward too — this is a fresh Event object,
              // so without this the next pass would see it unset and incorrectly
              // reopen Popup 1 whenever the checkbox is still unchecked.
              confirmed._insoleV1Confirmed = true;
              confirmed._popup2Confirmed = true;
              confirmed._includeInsole = e.detail?.includeInsole ?? insoleCommittedSoFar;
              confirmed._includeBag = e.detail?.includeBag ?? false;
              this.form.dispatchEvent(confirmed);
            }, { once: true });

            v1Module.dispatchEvent(new CustomEvent('insole-popup2:open', {
              bubbles: false,
              detail: { insoleCommitted: insoleCommittedSoFar }
            }));
            return;
          }
          // ───────────────────────────────────────────────────────────────────
        }

        // ── Insole V2 popup intercept ─────────────────────────────────────────
        const v2Module = document.querySelector('[data-addon-v2]');

        if (v2Module && !evt._insoleV2Confirmed) {
          document.addEventListener('insole-v2:confirm', (e) => {
            const confirmed = new Event('submit', { bubbles: true, cancelable: true });
            confirmed._insoleV2Confirmed = true;
            confirmed._includeInsole = e.detail?.includeInsole ?? false;
            this.form.dispatchEvent(confirmed);
          }, { once: true });

          v2Module.dispatchEvent(new CustomEvent('insole-v2:open', { bubbles: false }));
          return;
        }
        // ─────────────────────────────────────────────────────────────────────

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        try {
          const formData = new FormData(this.form);
          const mainVariantId = Number(formData.get('id'));
          const qtyInput = this.form.querySelector('[name="quantity"]');
          const qty = qtyInput ? Number(qtyInput.value || 1) : 1;

          const productTitle = this.dataset.productTitle || '';
          const giftHandle =
            this.dataset.giftHandle ||
            evt.submitter?.dataset.giftHandle ||
            '';

          const items = [{ id: mainVariantId, quantity: qty }];

          if (giftHandle) {
            const giftItem = await buildGiftItemFromHandle({
              handle: giftHandle,
              parentVariantId: mainVariantId,
              parentQty: qty,
              productTitle,
              formEl: this.form
            });
            console.log('[ProductForm] giftItem:', giftItem);
            if (giftItem) items.push(giftItem);
          }

          // ── Insole V1 addon + Wash bag addon ──────────────────────────────
          // When a wash bag is configured AND Popup 2 is actually reachable
          // (popup2/popup3 modes), Popup 2 is always the final gate, so its
          // _includeInsole/_includeBag flags are the source of truth. In
          // popup1 mode Popup 2 never runs at all (by design), so it must
          // fall back to the checkbox/Popup-1-only logic below, same as the
          // no-bag-configured case — otherwise a pre-checked checkbox is
          // silently ignored since nothing else ever sets _includeInsole.
          const shouldAddInsole = (bagConfigured && popupMode !== 'popup1')
            ? !!evt._includeInsole
            : (v1Opt2Checked || (!!evt._insoleV1Confirmed && !!evt._includeInsole));
          const shouldAddBag = !!evt._includeBag;

          // Both flags set at once uniquely identifies the "bundle" choice (in both
          // flow modes) — Popup 2's "wash bag only" decline-path case always has
          // shouldAddInsole === false, so it's never mistaken for a real bundle pick.
          const wantsBundle = shouldAddInsole && shouldAddBag;

          if (wantsBundle && bundleConfigured) {
            const bundleItem = await this._resolveBundleItem();
            if (bundleItem) {
              items.push(bundleItem);
              console.log('[ProductForm] Bundle item added to cart:', bundleItem);
            }
          } else {
            if (shouldAddInsole) {
              const insoleItem = await this.getUnisexInsoleItem(true);
              if (insoleItem) {
                items.push(insoleItem);
                console.log('[ProductForm] Insole item added to cart:', insoleItem);
              }
            }

            if (shouldAddBag) {
              const bagItem = await this._resolveBagItem();
              if (bagItem) {
                items.push(bagItem);
                console.log('[ProductForm] Wash bag item added to cart:', bagItem);
              }
            }
          }
          // ─────────────────────────────────────────────────────────────────

          // ── Free insole (gift row) ────────────────────────────────────────
          const freeInsoleItem = await this._resolveFreeInsoleItem(qty);
          if (freeInsoleItem) {
            items.push(freeInsoleItem);
            console.log('[ProductForm] Free insole added to cart:', freeInsoleItem);
          }
          // ─────────────────────────────────────────────────────────────────

          // ── Second add-on row ─────────────────────────────────────────────
          const addon2Item = await this._resolveAddon2Item(qty);
          if (addon2Item) {
            items.push(addon2Item);
            console.log('[ProductForm] Second add-on added to cart:', addon2Item);
          }
          // ─────────────────────────────────────────────────────────────────

          // ── Bundle swap ───────────────────────────────────────────────────
          const swapVariantId = await this._resolveSwapVariantId(mainVariantId);
          if (swapVariantId) items[0].id = swapVariantId;
          // ─────────────────────────────────────────────────────────────────

          // ── Insole V2 addon ───────────────────────────────────────────────
          if (v2Module && evt._insoleV2Confirmed && evt._includeInsole) {
            const v2InsoleItem = await this.getV2InsoleItem();
            if (v2InsoleItem) {
              items.push(v2InsoleItem);
              console.log('[ProductForm] V2 insole item added to cart:', v2InsoleItem);
            }
          }
          // ─────────────────────────────────────────────────────────────────

          if (!this.cart) {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items })
            });
            window.location = window.routes.cart_url;
            return;
          }

          const sectionsToRender = this.cart.getSectionsToRender?.() || [];
          const sectionIds = sectionsToRender.map(s => s.id);

          const payload = { items };
          if (sectionIds.length > 0) {
            payload.sections = sectionIds;
            payload.sections_url = window.location.pathname;
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          }).then(r => r.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: mainVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);
            this.error = true;
            return;
          }

          this.error = false;

          // ── Force checkout redirect ───────────────────────────────────────
          if (v2Module || this.forceCheckout) {
            window.location = '/checkout';
            return;
          }
          // ─────────────────────────────────────────────────────────────────

          if (this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);
          const renderPayload = response;

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => { setTimeout(() => { this.cart.renderContents(renderPayload); }); },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(renderPayload);
          }

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: mainVariantId,
              cartData: renderPayload,
            });
          }

        } catch (e) {
          console.error('[ProductForm] onSubmitHandler error:', e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
